/**
 * router/routes.js
 *
 * Daftar semua route Portal IHC, dikelompokkan per area:
 *   - Authentication Routes (Public): login, register, forgot-password
 *   - Main Application (Protected):
 *       • Dashboard
 *       • Master Data: access, analysis-form, branch, medicine, intervention,
 *                      assessment, service-salary, bank
 *       • Settings:    reason, transportation, payroll-schedule,
 *                      attendance-schedule
 *
 * Konvensi:
 * - Semua route protected wajib pakai `meta.authRequired: true`.
 * - `meta.title` wajib diisi (dipakai untuk update document.title oleh
 *   beforeResolve di router/index.js).
 * - Component pakai dynamic import (`() => import(...)`) supaya code-splitting
 *   otomatis per route.
 *
 * Cara tambah route baru: ikuti pattern existing, kelompokkan di bawah
 * komentar section yang sesuai (MASTER DATA / SETTINGS / dst).
 */
export default [
  // ==============================
  // Authentication Routes (Public)
  // ==============================
  {
    path: "/login",
    name: "login",
    component: () => import("../views/account/login.vue"),
    meta: {
      title: "Login",
    },
  },
  {
    path: "/register",
    name: "Register",
    component: () => import("../views/account/register.vue"),
    meta: {
      title: "Register",
    },
  },
  {
    path: "/forgot-password",
    name: "Forgot password",
    component: () => import("../views/account/forgot-password.vue"),
    meta: {
      title: "Forgot Password",
    },
  },

  // ==============================
  // Main Application Routes (Protected)
  // ==============================
  {
    path: "/",
    name: "default",
    meta: {
      title: "Dashboard",
      authRequired: true,
    },
    component: () => import("../views/dashboard/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                 MASTER DATA                                */
  /* -------------------------------------------------------------------------- */
  {
    path: "/master-data/access",
    name: "access",
    meta: {
      title: "Hak Akses",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/index.vue"),
  },
  {
    path: "/master-data/access/role/tambah",
    name: "role-tambah",
    meta: {
      title: "Tambah Role",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/components/role/FormRole.vue"),
  },
  {
    path: "/master-data/access/role/edit/:id",
    name: "role-edit",
    meta: {
      title: "Edit Role",
      authRequired: true,
    },
    component: () => import("../views/master-data/access/components/role/FormRole.vue"),
  },
  {
    path: "/master-data/analysis-form",
    name: "analysis-form",
    meta: {
      title: "Form Analisa",
      authRequired: true,
    },
    component: () => import("../views/master-data/analysis-form/index.vue"),
  },
  {
    path: "/master-data/branch",
    name: "branch",
    meta: {
      title: "Cabang",
      authRequired: true,
    },
    component: () => import("../views/master-data/branch/index.vue"),
  },
  {
    path: '/master-data/medicine',
    name: 'medicine',
    meta: {
      title: 'Obat',
      authRequired: true,
    },
    component: () => import('../views/master-data/medicine/index.vue'),
  },
  {
    path: '/master-data/intervention',
    name: 'intervention',
    meta: {
      title: 'Intervensi',
      authRequired: true,
    },
    component: () => import('../views/master-data/intervention/index.vue'),
  },
  {
    path: '/master-data/assessment',
    name: 'assessment',
    meta: {
      title: 'Asesmen',
      authRequired: true,
    },
    component: () => import('../views/master-data/assessment/index.vue'),
  },
  {
    path: '/master-data/service-salary',
    name: 'service-salary',
    meta: {
      title: 'Gaji Layanan',
      authRequired: true,
    },
    component: () => import('../views/master-data/service-salary/index.vue'),
  },
  {
    path: "/master-data/bank",
    name: "bank",
    meta: {
      title: "Bank",
      authRequired: true,
    },
    component: () => import("../views/master-data/bank/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  SETTINGS                                  */
  /* -------------------------------------------------------------------------- */
  {
    path: "/settings/reason",
    name: "reason",
    meta: {
      title: "Kebijakan Alasan Stop",
      authRequired: true,
    },
    component: () => import("../views/settings/reason/index.vue"),
  },
  {
    path: "/settings/transportation",
    name: "transportation",
    meta: {
      title: "Kebijakan Transportasi",
      authRequired: true,
    },
    component: () => import("../views/settings/transportation/index.vue"),
  },
  {
    path: "/settings/payroll-schedule",
    name: "payroll-schedule",
    meta: {
      title: "Kebijakan Jadwal Penggajian",
      authRequired: true,
    },
    component: () => import("../views/settings/payroll-schedule/index.vue"),
  },
  {
    path: "/settings/attendance-schedule",
    name: "attendance-schedule",
    meta: {
      title: "Kebijakan Jadwal Absensi",
      authRequired: true,
    },
    component: () => import("../views/settings/attendance-schedule/index.vue"),
  },
  {
    path: "/settings/time-off",
    name: "time-off",
    meta: {
      title: "Kebijakan Kehadiran",
      authRequired: true,
    },
    component: () => import("../views/settings/time-off/index.vue"),
  },
  {
    path: "/settings/compensation",
    name: "compensation",
    meta: {
      title: "Kebijakan Kompensasi",
      authRequired: true,
    },
    component: () => import("../views/settings/compensation/index.vue"),
  },

  /* -------------------------------------------------------------------------- */
  /*                                  PERSONNEL                                 */
  /* -------------------------------------------------------------------------- */
  {
    path: "/personnel/employee",
    name: "employee",
    meta: {
      title: "Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/index.vue"),
  },
  {
    path: "/personnel/employee/create",
    name: "employee-create",
    meta: {
      title: "Tambah Karyawan",
      authRequired: true,
    },
    component: () => import("../views/personnel/employee/create/index.vue"),
  },
  {
    path: "/personnel/credential",
    name: "credential",
    meta: {
      title: "Kredensial",
      authRequired: true,
    },
    component: () => import("../views/personnel/credential/index.vue"),
  },
  {
    path: "/personnel/career",
    name: "career",
    meta: {
      title: "Karir",
      authRequired: true,
    },
    component: () => import("../views/personnel/career/index.vue"),
  },
  {
    path: "/personnel/applicant",
    name: "applicant",
    meta: {
      title: "Pelamar",
      authRequired: true,
    },
    component: () => import("../views/personnel/applicant/index.vue"),
  },
  {
    path: "/personnel/job-position",
    name: "job-position",
    meta: {
      title: "Jabatan",
      authRequired: true,
    },
    component: () => import("../views/personnel/job-position/index.vue"),
  },
  {
    path: "/personnel/warning-letter",
    name: "warning-letter",
    meta: {
      title: "Surat Peringatan",
      authRequired: true,
    },
    component: () => import("../views/personnel/warning-letter/index.vue"),
  },

  // ==============================
  // TODO: Tambahkan route fitur Portal IHC di sini
  // Contoh:
  // {
  //   path: "/pasien",
  //   name: "Pasien",
  //   meta: { title: "Pasien", authRequired: true },
  //   component: () => import("../views/pasien/index.vue"),
  // },
  // ==============================
  // routes.js — paling bawah!
  {
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () => import("../views/errors/404.vue"),
  },
];